import {
  DefaultPluginStoreModel,
  PluginStoreModel,
  IPluginStoreData,
  PluginModelVersion,
} from "../../store/plugin.store.model";
import { IModelView, VaultModelView } from "../model.view";

/**
 * Plugin View data type
 */
export type PluginViewObject = {
  /** Plugin Name */
  name: string;
  /** Linked Key ID for authentication */
  keyId: string;
  /** Plugin URL */
  url: string;
  /** Plugin-specific settings blob (persisted as a JSON string in `opt`). */
  options?: Record<string, unknown>;
};

/**
 * Public Plugin View
 *
 * Represents a plugin with associated metadata but without exposing vault internals
 */
export type PluginView = PluginViewObject & IModelView<PluginModelVersion, IPluginStoreData>;

/**
 * Creates a PluginView instance from a store model
 */
export const fromStoreModel = (model: PluginStoreModel): PluginView => {
  return new PluginVaultView(model);
};

/**
 * Creates a PluginView instance from plugin data
 */
export const fromView = (view: PluginViewObject | PluginView): PluginView => {
  if (isPluginView(view)) {
    return fromStoreModel(view.toStoreModel());
  }

  return fromStoreModel({
    id: crypto.randomUUID(),
    dc: new Date().toISOString(),
    dm: new Date().toISOString(),
    v: DefaultPluginStoreModel.v,
    vid: "",
    nm: view.name,
    kid: view.keyId,
    url: view.url,
    opt: view.options ? JSON.stringify(view.options) : undefined,
  });
};

const isPluginView = (obj: any): obj is PluginView => {
  return !!obj && typeof (obj as PluginView).toStoreModel === "function";
};

/**
 * Plugin Vault View
 *
 * Internal implementation class for PluginView.
 * Do not expose this class publicly or use directly outside of fromView methods.
 * The vaultId property is intentionally protected to prevent external access.
 */
class PluginVaultView
  extends VaultModelView<PluginModelVersion, IPluginStoreData>
  implements PluginViewObject
{
  readonly name: string;
  readonly keyId: string;
  readonly url: string;
  readonly options?: Record<string, unknown>;

  constructor(model: PluginStoreModel) {
    super(model);
    this.name = model.nm;
    this.keyId = model.kid;
    this.url = model.url;
    this.options = parseOptions(model.opt);
  }

  protected toDataModel() {
    return {
      nm: this.name,
      kid: this.keyId,
      url: this.url,
      opt: this.options ? JSON.stringify(this.options) : undefined,
    };
  }
}

/** Safely parse the persisted `opt` JSON blob; malformed data reads as absent. */
const parseOptions = (opt?: string): Record<string, unknown> | undefined => {
  if (!opt) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(opt);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
};
